/* Member Statement — cross-scheme passbook for a single member.
   Filters: member (ITS or name typeahead), scheme, date from / to.
   Shows opening balance (everything before "from"), an in-range ledger with
   running balance + maturity (when commitment), and a closing balance KPI.
   Member Balances — cross-tab report (member × scheme). Each scheme column
   gets a deterministic pastel tint so different schemes are visually
   distinct. Clicking a balance cell deep-links into the Member Statement
   pre-filtered to that member + scheme. */
const { useState: useStateMS, useEffect: useEffectMS, useMemo: useMemoMS } = React;

// ───────── Member Statement ─────────

function MemberStatementPage({ params }) {
  // Optional deep-link: ['itsNo'] or ['itsNo', 'schemeId']. Used when arriving
  // from the Member Balances cross-tab.
  const presetItsNo = params?.[0] ? Number(params[0]) : null;
  const presetSchemeId = params?.[1] || '';

  const [member, setMember] = useStateMS(null);
  const [schemeFilter, setSchemeFilter] = useStateMS(presetSchemeId);
  // Default: this calendar year-to-date. The user can widen / narrow.
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = today.slice(0, 4) + '-01-01';
  const [from, setFrom] = useStateMS(yearStart);
  const [to, setTo] = useStateMS(today);
  const [txs, setTxs] = useStateMS([]); // ALL of the member's tx, no date filter
  const [loading, setLoading] = useStateMS(false);
  const [err, setErr] = useStateMS('');
  // Bumped by the refresh button to force a re-fetch even when the
  // member selection hasn't changed.
  const [tick, setTick] = useStateMS(0);

  // Auto-resolve member from a preset ITS — fired once on mount and any
  // time the deep-link points at a different member.
  useEffectMS(() => {
    if (!presetItsNo) return;
    if (member?.itsNo === presetItsNo) return;
    let cancel = false;
    window.QH_API.get(`/members/${presetItsNo}`)
      .then(m => { if (!cancel) setMember(m); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [presetItsNo]);

  // Re-apply the scheme preset whenever the deep-link changes (the user
  // can override afterwards).
  useEffectMS(() => { setSchemeFilter(presetSchemeId); }, [presetSchemeId]);

  useEffectMS(() => {
    let cancel = false;
    setTxs([]); setErr('');
    if (!member?.itsNo) return;
    setLoading(true);
    // Pull every transaction for this member across schemes — server-side
    // scheme filtering would also work but we already pull the full list
    // for the cross-scheme view, so a single fetch + client filtering keeps
    // the contract simple and avoids a round-trip when the user toggles the
    // scheme filter.
    window.QH_API.get(`/transactions?memberItsNo=${member.itsNo}&pageSize=10000`)
      .then(r => { if (!cancel) setTxs(r.items || []); })
      .catch(e => { if (!cancel) setErr(e.message || 'Failed to load statement.'); })
      .finally(() => { if (!cancel) setLoading(false); });
    // tick is intentionally part of the dep array so the refresh button
    // re-fires this effect for the same member.
    return () => { cancel = true; };
  }, [member?.itsNo, tick]);

  const dateOf = (t) => (t.date || '').slice(0, 10);
  const signed = (t) => (t.type === 'deposit' ? +Number(t.amount) : -Number(t.amount));

  // Apply scheme filter first (if set), then sort + split by date.
  const scoped = schemeFilter ? txs.filter(t => t.schemeId === schemeFilter) : txs;
  const sorted = [...scoped].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const before = sorted.filter(t => dateOf(t) < from);
  const inRange = sorted.filter(t => {
    const d = dateOf(t);
    return d >= from && d <= to;
  });
  const opening = before.reduce((s, t) => s + signed(t), 0);
  let running = opening;
  const rows = inRange.map(t => {
    running += signed(t);
    return { ...t, _balance: running };
  });
  const closing = running;
  const totalDep = inRange.filter(t => t.type === 'deposit').reduce((s, t) => s + Number(t.amount), 0);
  const totalWd = inRange.filter(t => t.type === 'withdrawal').reduce((s, t) => s + Number(t.amount), 0);

  const schemeLabel = (id) => {
    const s = (window.QH_DATA?.schemes || []).find(x => x.id === id);
    return s ? `${s.name}` : id || '—';
  };
  const schemeCode = (id) => {
    const s = (window.QH_DATA?.schemes || []).find(x => x.id === id);
    return s?.code || '';
  };
  const eventLabel = (id) => {
    if (!id) return '—';
    const e = (window.QH_DATA?.events || []).find(x => x.id === id);
    return e?.name || id;
  };

  // Schemes the member has actually transacted in — drives the filter
  // dropdown so we don't show empty options.
  const schemesUsed = useMemoMS(() => {
    const ids = new Set(txs.map(t => t.schemeId));
    return (window.QH_DATA?.schemes || []).filter(s => ids.has(s.id));
  }, [txs]);

  const exportCsv = () => {
    if (!member) return;
    const lines = [];
    lines.push(['Date', 'Scheme', 'Event', 'Type', 'Amount BHD', 'Maturity', 'Balance BHD', 'Comment'].join(','));
    lines.push(['', '', 'Opening balance', '', '', '', opening.toFixed(3), ''].join(','));
    for (const t of rows) {
      lines.push([
        dateOf(t),
        '"' + (schemeLabel(t.schemeId) + (schemeCode(t.schemeId) ? ' ' + schemeCode(t.schemeId) : '')).replace(/"/g, '""') + '"',
        '"' + eventLabel(t.eventId).replace(/"/g, '""') + '"',
        t.type,
        (t.type === 'deposit' ? '+' : '-') + Number(t.amount).toFixed(3),
        t.maturity || '',
        t._balance.toFixed(3),
        '"' + (t.note || '').replace(/"/g, '""') + '"',
      ].join(','));
    }
    lines.push(['', '', 'Closing balance', '', '', '', closing.toFixed(3), ''].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const tag = schemeFilter ? `-${schemeCode(schemeFilter) || schemeFilter}` : '';
    a.href = url;
    a.download = `statement-${member.itsNo}${tag}-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Directory</span><I.ChevronRight s={10}/><span>Member Statement</span></div>
          <h1>Member Statement</h1>
          <div className="subtitle">Per-member passbook with running balance and maturity flags. Pick a scheme to scope the view.</div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => setTick(t => t + 1)} disabled={!member} title="Reload statement"><I.Refresh/></button>
          <button className="btn" onClick={exportCsv} disabled={!member || rows.length === 0}><I.Download/> Export CSV</button>
        </div>
      </div>

      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        <div className="card">
          <div className="card-body" style={{display:'grid',gridTemplateColumns:'2fr 1.2fr 1fr 1fr',gap:12,alignItems:'flex-end'}}>
            <window.MemberSearchPicker value={member} onChange={setMember}/>
            <div className="field">
              <label>Scheme</label>
              <select className="select" value={schemeFilter} onChange={e => setSchemeFilter(e.target.value)} disabled={!member}>
                <option value="">All schemes</option>
                {schemesUsed.map(s => <option key={s.id} value={s.id}>{s.name} · {s.code}</option>)}
              </select>
            </div>
            <div className="field">
              <label>From</label>
              <input className="input font-mono" type="date" value={from} onChange={e => setFrom(e.target.value)}/>
            </div>
            <div className="field">
              <label>To</label>
              <input className="input font-mono" type="date" value={to} onChange={e => setTo(e.target.value)}/>
            </div>
          </div>
        </div>

        {!member && (
          <div className="card"><div className="card-body" style={{padding:30,textAlign:'center',color:'var(--ink-3)',fontSize:13}}>
            Pick a member to load their statement.
          </div></div>
        )}

        {member && (
          <>
            <KPIStrip items={[
              { label: 'Opening balance', value: fmtBHD(opening) + ' BHD', sub: 'as on ' + from },
              { label: 'Period deposits', value: '+' + fmtBHD(totalDep) + ' BHD', sub: inRange.filter(t => t.type === 'deposit').length + ' entries' },
              { label: 'Period withdrawals', value: '−' + fmtBHD(totalWd) + ' BHD', sub: inRange.filter(t => t.type === 'withdrawal').length + ' entries' },
              { label: 'Closing balance', value: fmtBHD(closing) + ' BHD', sub: 'as on ' + to },
            ]}/>

            <div className="card">
              <div className="card-header">
                {member.fullName} · ITS <span className="font-mono">{member.itsNo}</span>
                {schemeFilter && <> · scheme <b>{schemeLabel(schemeFilter)}</b> <span className="muted font-mono text-xs">{schemeCode(schemeFilter)}</span></>}
                <span className="actions"><span className="muted text-xs">{from} → {to} · {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}</span></span>
              </div>
              <div className="table-wrap" style={{border:'none',borderRadius:0,maxHeight:560,overflow:'auto'}}>
                <table className="data">
                  <thead style={{position:'sticky',top:0,background:'var(--bg-elev)',zIndex:1}}>
                    <tr>
                      <th>Date</th>
                      <th>Scheme</th>
                      <th>Event</th>
                      <th>Comment</th>
                      <th className="right">Deposit</th>
                      <th className="right">Withdrawal</th>
                      <th className="right">Balance · BHD</th>
                      <th>Maturity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening row pinned at the top of the ledger */}
                    <tr style={{background:'var(--bg-sunken)'}}>
                      <td className="font-mono text-xs">{from}</td>
                      <td colSpan={3} className="text-xs muted" style={{fontStyle:'italic'}}>Opening balance</td>
                      <td/><td/>
                      <td className="num right" style={{fontWeight:600}}>{fmtBHD(opening)}</td>
                      <td/>
                    </tr>
                    {rows.map(t => (
                      <tr key={t.id}>
                        <td className="font-mono text-xs">{dateOf(t)}</td>
                        <td>
                          <div style={{fontSize:12}}>{schemeLabel(t.schemeId)}</div>
                          <div className="text-xs muted font-mono">{schemeCode(t.schemeId)}</div>
                        </td>
                        <td className="text-xs">{eventLabel(t.eventId)}</td>
                        <td className="text-xs muted">{t.note || '—'}</td>
                        <td className="num right pos">{t.type === 'deposit' ? '+' + fmtBHD(t.amount) : ''}</td>
                        <td className="num right neg">{t.type === 'withdrawal' ? '−' + fmtBHD(t.amount) : ''}</td>
                        <td className="num right" style={{fontWeight:600}}>{fmtBHD(t._balance)}</td>
                        <td className="font-mono text-xs muted">
                          {t.maturity
                            ? <span title={t.commitmentMonths ? `${t.commitmentMonths}m commitment` : ''}>{t.maturity}</span>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && !loading && (
                      <tr><td colSpan={8} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No transactions in this date range.</td></tr>
                    )}
                    {/* Closing row */}
                    <tr style={{background:'var(--bg-sunken)'}}>
                      <td className="font-mono text-xs">{to}</td>
                      <td colSpan={3} className="text-xs muted" style={{fontStyle:'italic'}}>Closing balance</td>
                      <td/><td/>
                      <td className="num right" style={{fontWeight:700}}>{fmtBHD(closing)}</td>
                      <td/>
                    </tr>
                  </tbody>
                </table>
              </div>
              {loading && <div style={{padding:14,color:'var(--ink-3)',fontSize:12}}>Loading…</div>}
              {err && <div style={{padding:14,color:'var(--neg)',fontSize:12}}>{err}</div>}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ───────── Member Balances cross-tab ─────────

// Deterministic pastel tint per scheme index — keeps the same scheme in
// the same colour across renders. Header tone is darker than cell tone so
// the column band is distinguishable from its data. Mirrors the HSL sweep
// in the server's xlsx export.
function schemeHeaderColor(i) { return `hsl(${(i * 47) % 360}, 55%, 78%)`; }
function schemeCellColor(i)   { return `hsl(${(i * 47) % 360}, 55%, 94%)`; }

function MemberBalancesPage({ go }) {
  const [data, setData] = useStateMS({ schemes: [], rows: [] });
  const [q, setQ] = useStateMS('');
  const [activeOnly, setActiveOnly] = useStateMS(false);
  const [loading, setLoading] = useStateMS(true);
  const [err, setErr] = useStateMS('');
  const [tick, setTick] = useStateMS(0);

  useEffectMS(() => {
    let cancel = false;
    setLoading(true); setErr('');
    window.QH_API.get('/reports/member-balances')
      .then(r => { if (!cancel) setData(r); })
      .catch(e => { if (!cancel) setErr(e.message || 'Failed to load report.'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [tick]);

  const filtered = data.rows.filter(r => {
    if (activeOnly && !r.active) return false;
    if (!q) return true;
    const ql = q.toLowerCase();
    return String(r.itsNo).includes(ql)
        || (r.fullName || '').toLowerCase().includes(ql)
        || (r.mobile || '').toLowerCase().includes(ql);
  });

  // Per-scheme totals (sum of column) — useful for sanity at the bottom.
  const totals = data.schemes.map(s => filtered.reduce((sum, r) => sum + Number(r.balances[s.id] || 0), 0));
  const grandTotal = totals.reduce((s, t) => s + t, 0);

  const exportXlsx = () => {
    // The server already produces a coloured xlsx that mirrors the
    // on-screen palette — reuse it instead of re-implementing in the
    // browser. The download flows through the same authed helper as
    // every other CSV export.
    window.safeDownload('/reports/member-balances/export.xlsx', `member-balances-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Directory</span><I.ChevronRight s={10}/><span>Member Balances</span></div>
          <h1>Member Balances</h1>
          <div className="subtitle">Cross-tab of every member's balance per scheme. Click a balance to drill into that member's statement for that scheme.</div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => setTick(t => t + 1)} title="Reload data"><I.Refresh/></button>
          <button className="btn" onClick={exportXlsx} disabled={loading || data.rows.length === 0}><I.Download/> Export Excel</button>
        </div>
      </div>

      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        <div className="toolbar">
          <input className="input" style={{height:28,width:260}} placeholder="Search ITS / name / mobile…" value={q} onChange={e => setQ(e.target.value)}/>
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,marginLeft:8,cursor:'pointer'}}>
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)}/>
            Active only
          </label>
          <div className="grow"/>
          <span className="muted text-xs">{filtered.length} of {data.rows.length} members · {data.schemes.length} schemes</span>
        </div>

        <div className="table-wrap" style={{maxHeight:640,overflow:'auto'}}>
          <table className="data">
            <thead style={{position:'sticky',top:0,zIndex:1,background:'var(--bg-elev)'}}>
              <tr>
                <th style={{minWidth:90}}>ITS</th>
                <th style={{minWidth:200}}>Name</th>
                <th style={{minWidth:130}}>Mobile</th>
                <th>Status</th>
                {data.schemes.map((s, i) => (
                  <th key={s.id} className="right"
                      style={{background: schemeHeaderColor(i), borderBottom: `2px solid hsl(${(i*47)%360}, 60%, 60%)`, minWidth:130}}>
                    <div style={{fontSize:12,fontWeight:600}}>{s.name}</div>
                    <div className="text-xs muted font-mono" style={{fontWeight:400}}>{s.code}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.itsNo}>
                  <td className="font-mono text-xs">{r.itsNo}</td>
                  <td>{r.fullName}</td>
                  <td className="font-mono text-xs">{r.mobile || '—'}</td>
                  <td><span className={`tag ${r.active ? 'pos' : ''}`}>{r.active ? 'active' : 'inactive'}</span></td>
                  {data.schemes.map((s, i) => {
                    const b = Number(r.balances[s.id] || 0);
                    const has = !!r.balances[s.id];
                    return (
                      <td key={s.id} className="num right"
                          style={{
                            background: schemeCellColor(i),
                            cursor: has ? 'pointer' : 'default',
                            fontWeight: has ? 600 : 400,
                            color: has ? 'var(--ink-1)' : 'var(--ink-3)',
                          }}
                          onClick={has ? () => go && go(`memberStatement:${r.itsNo}:${s.id}`) : undefined}
                          title={has ? `Open statement for ${r.fullName} in ${s.name}` : undefined}>
                        {has ? fmtBHD(b) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={4 + data.schemes.length} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No members match the filters.</td></tr>
              )}
            </tbody>
            {/* Real <tfoot> beats sticky-positioned <tr> in <tbody> — the
                row always sits at the bottom of the table, no overflow
                gymnastics. Two rows: per-scheme totals + a grand total
                strip above it that sums every scheme column. */}
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{background:'var(--bg-sunken)'}}>
                  <td colSpan={4} className="text-xs muted" style={{fontStyle:'italic',fontWeight:600}}>Total · {filtered.length} member{filtered.length === 1 ? '' : 's'}</td>
                  {data.schemes.map((s, i) => (
                    <td key={s.id} className="num right" style={{background: schemeHeaderColor(i), fontWeight:700}}>
                      {fmtBHD(totals[i])}
                    </td>
                  ))}
                </tr>
                <tr style={{background:'var(--bg-elev)',borderTop:'2px solid var(--line)'}}>
                  <td colSpan={4} style={{fontWeight:700,textTransform:'uppercase',fontSize:11,letterSpacing:0.4}}>Grand total · all schemes</td>
                  <td className="num right" colSpan={data.schemes.length} style={{fontWeight:700,fontSize:14}}>
                    {fmtBHD(grandTotal)} BHD
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {loading && <div style={{padding:14,color:'var(--ink-3)',fontSize:12}}>Loading…</div>}
        {err && <div style={{padding:14,color:'var(--neg)',fontSize:12}}>{err}</div>}
      </div>
    </>
  );
}

window.MemberStatementPage = MemberStatementPage;
window.MemberBalancesPage = MemberBalancesPage;
